// src/components1/AnimatedCounter.js
import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter:
 * Smoothly rolls/counts up numeric values to their final target value
 * with an ease-out curve whenever the target value or currency changes.
 *
 * @param {number} value - Target amount to animate to
 * @param {string} currency - Currency symbol prefix (e.g. "$", "₹")
 * @param {number} duration - Animation duration in ms (default 900ms)
 * @param {number} decimals - Number of decimal places (default 2)
 * @param {string} className - Optional wrapper CSS class
 */
const AnimatedCounter = ({
  value = 0,
  currency = "$",
  duration = 950,
  decimals = 2,
  className = "",
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startValRef = useRef(0);
  const startTimeRef = useRef(null);
  const animFrameRef = useRef(null);
  const targetVal = Number(value) || 0;

  useEffect(() => {
    const startVal = startValRef.current;
    const diff = targetVal - startVal;

    // If change is negligible, update immediately without animation
    if (Math.abs(diff) < 0.001) {
      setDisplayValue(targetVal);
      startValRef.current = targetVal;
      return;
    }

    startTimeRef.current = null;

    // Quartic ease-out function for smooth rolling deceleration
    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

    const step = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      const easedProgress = easeOutQuart(progress);
      const current = startVal + diff * easedProgress;

      setDisplayValue(current);

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(step);
      } else {
        setDisplayValue(targetVal);
        startValRef.current = targetVal;
      }
    };

    animFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [targetVal, duration]);

  const formatted = displayValue.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span className={className}>
      {currency}
      {formatted}
    </span>
  );
};

export default AnimatedCounter;
